/**
 * NavGroupingTour — one-time spotlight that highlights the three new
 * dropdown groups (Learn / Watch / Tools) so returning users can find
 * where their old menu items moved.
 *
 * Triggered on the first visit AFTER iter-99 nav regrouping. Stores the
 * acknowledgement in localStorage as `nav-tour:v1:dismissed`. Renders
 * absolutely-positioned dialogues anchored to each `data-testid='nav-group-*'`
 * button, with prev/next controls and a "Got it" final dismissal.
 *
 * Mobile (≤768px) skips the tour — the mobile drawer already labels each
 * section header inline, so there's nothing to discover.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowRight, ArrowLeft, Sparkles, X } from "lucide-react";

const STORAGE_KEY = "nav-tour:v1:dismissed";

const STEPS = [
  {
    target: "nav-group-learn",
    title: "Learn",
    body: "Library, Glossary, Newsletters and Guide live here now — one click instead of four menu items.",
  },
  {
    target: "nav-group-watch",
    title: "Watch",
    body: "Webinars, Speakers, Shorts and AI Eval — your video & expert hub.",
  },
  {
    target: "nav-group-tools",
    title: "Tools",
    body: "All Tools, Carousel Lite and Finder — utilities to ship faster.",
  },
];

function getAnchorRect(testId) {
  if (typeof document === "undefined") return null;
  const el = document.querySelector(`[data-testid='${testId}']`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

export default function NavGroupingTour() {
  const [active, setActive] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [anchor, setAnchor] = useState(null);

  // Decide whether to run the tour on mount.
  useEffect(() => {
    if (typeof window === "undefined") return;
    // Skip on small screens — the mobile drawer is already labelled.
    if (window.innerWidth < 768) return;
    try {
      if (window.localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch { /* localStorage blocked — show tour anyway */ }
    // Defer two animation frames so the navbar's render has settled and
    // we can measure the target buttons.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setActive(true));
    });
  }, []);

  // Recompute anchor position whenever step / viewport changes.
  useEffect(() => {
    if (!active) return undefined;
    const measure = () => {
      const rect = getAnchorRect(STEPS[stepIdx].target);
      setAnchor(rect ? {
        top: rect.bottom + window.scrollY + 10,
        left: rect.left + window.scrollX + rect.width / 2,
      } : null);
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, { passive: true });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure);
    };
  }, [active, stepIdx]);

  // Dismiss the tour when the user clicks anywhere outside the bubble.
  // Implemented with a window-level click listener so the rest of the
  // page stays interactive — clicking a nav item dismisses + navigates
  // in one step, exactly the discovery moment the tour aims for.
  useEffect(() => {
    if (!active) return undefined;
    const onDocClick = (e) => {
      const bubble = document.querySelector("[data-testid='nav-tour-bubble']");
      if (bubble && bubble.contains(e.target)) return;
      try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
      setActive(false);
    };
    // Defer one tick so the mount click doesn't immediately self-dismiss.
    const id = window.setTimeout(() => {
      document.addEventListener("click", onDocClick, true);
    }, 100);
    return () => {
      window.clearTimeout(id);
      document.removeEventListener("click", onDocClick, true);
    };
  }, [active]);

  const dismiss = () => {
    try { window.localStorage.setItem(STORAGE_KEY, "1"); } catch { /* noop */ }
    setActive(false);
  };

  if (!active || !anchor) return null;

  const step = STEPS[stepIdx];
  const isLast = stepIdx === STEPS.length - 1;
  const isFirst = stepIdx === 0;

  const bubble = (
    <>
      {/* Subtle dim — purely cosmetic, does NOT block page interaction.
          Click anywhere outside the bubble to dismiss is wired via a
          window listener (below). */}
      <div
        aria-hidden
        className="fixed inset-0 z-[60] bg-black/20 backdrop-blur-[1px] pointer-events-none"
        data-testid="nav-tour-overlay"
      />
      <div
        role="dialog"
        aria-label={`Quick tour step ${stepIdx + 1} of ${STEPS.length}`}
        style={{
          position: "absolute",
          top: anchor.top,
          left: anchor.left,
          transform: "translateX(-50%)",
        }}
        className="z-[70] w-[300px] rounded-xl border border-rp-accent/40 bg-rp-surface shadow-xl shadow-rp-accent/10 p-4"
        data-testid="nav-tour-bubble"
        onClick={(e) => e.stopPropagation()}
      >
        {/* arrow */}
        <span
          aria-hidden
          className="absolute -top-2 left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 bg-rp-surface border-l border-t border-rp-accent/40"
        />
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.18em] text-rp-accent inline-flex items-center gap-1">
            <Sparkles className="h-3 w-3" /> New grouping · {stepIdx + 1}/{STEPS.length}
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-rp-textFaint hover:text-rp-text"
            aria-label="Close tour"
            data-testid="nav-tour-close"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <h3
          className="font-display text-base font-bold text-rp-text"
          data-testid={`nav-tour-title-${stepIdx}`}
        >
          {step.title}
        </h3>
        <p className="text-sm text-rp-textDim mt-1.5">
          {step.body}
        </p>
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-rp-border">
          {!isFirst ? (
            <button
              type="button"
              onClick={() => setStepIdx((i) => Math.max(0, i - 1))}
              className="text-xs text-rp-textDim hover:text-rp-text inline-flex items-center gap-1"
              data-testid="nav-tour-prev"
            >
              <ArrowLeft className="h-3 w-3" /> Back
            </button>
          ) : (
            <span className="text-[11px] text-rp-textFaint">First step</span>
          )}
          {isLast ? (
            <button
              type="button"
              onClick={dismiss}
              className="text-xs font-semibold rounded-md bg-rp-accent hover:bg-rp-accentHover text-white px-3 py-1.5 inline-flex items-center gap-1"
              data-testid="nav-tour-done"
            >
              Got it
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStepIdx((i) => Math.min(STEPS.length - 1, i + 1))}
              className="text-xs font-semibold rounded-md bg-rp-accent hover:bg-rp-accentHover text-white px-3 py-1.5 inline-flex items-center gap-1"
              data-testid="nav-tour-next"
            >
              Next <ArrowRight className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>
    </>
  );

  // Render into <body> so the dim overlay sits above sticky elements.
  return createPortal(bubble, document.body);
}
